import { useState } from "react";
import { withClientContext } from "~/apollo/client";
import {
  useCancelDdqProdOpsWorkflowMutation,
  useHoldDdqProdOpsWorkflowMutation,
  useRestartDdqProdOpsWorkflowMutation,
} from "~/graphql/supergraphHooks";

export type UseWorkflowActionsResult = {
  onHold: (comment: string) => void;
  onRestart: () => void;
  onCancel: () => void;
  isHolding: boolean;
  isRestarting: boolean;
  isCancelling: boolean;
  holdError: string | null;
  restartError: string | null;
  cancelError: string | null;
};

export const useWorkflowActions = (
  workflowId: string,
  onHoldSucceeded: () => void,
  onCancelSucceeded: () => void,
): UseWorkflowActionsResult => {
  const [holdError, setHoldError] = useState<string | null>(null);
  const [restartError, setRestartError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // The queue row's status/dueAt/onHoldAt move with the transition.
  const [holdWorkflow, { loading: isHolding }] = useHoldDdqProdOpsWorkflowMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });
  const [restartWorkflow, { loading: isRestarting }] = useRestartDdqProdOpsWorkflowMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });
  const [cancelWorkflow, { loading: isCancelling }] = useCancelDdqProdOpsWorkflowMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });

  const onHold = (comment: string) => {
    setHoldError(null);
    holdWorkflow({ variables: { workflowId, comment } })
      .then(onHoldSucceeded)
      .catch((error: unknown) => {
        setHoldError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  const onRestart = () => {
    setRestartError(null);
    restartWorkflow({ variables: { workflowId } }).catch((error: unknown) => {
      setRestartError(error instanceof Error ? error.message : "Something went wrong.");
    });
  };

  const onCancel = () => {
    setCancelError(null);
    cancelWorkflow({ variables: { workflowId } })
      .then(onCancelSucceeded)
      .catch((error: unknown) => {
        setCancelError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  return {
    onHold,
    onRestart,
    onCancel,
    isHolding,
    isRestarting,
    isCancelling,
    holdError,
    restartError,
    cancelError,
  };
};
